import { type RovoContentBridgeAPI } from './rovo-bridge-api';
import { isSameOrigin } from './transport/transports/browser-apis';

/* eslint-disable no-console */

declare global {
	interface Window {
		/**
		 * @description Used internally by @atlassian/rovo-content-bridge-api. DO NOT USE.
		 * @deprecated
		 */
		__rovoContentBridgeAPI__: RovoContentBridgeAPI | undefined;

		/**
		 * @description Used internally by @atlassian/rovo-content-bridge-api. DO NOT USE.
		 * @deprecated
		 */
		__rovoBridgeDebuggerStart__: VoidFunction | undefined;
	}
}

/**
 * Attaches a given bridge API instance to the {@link window.top}.
 *
 * @description In same-origin contexts, the bridge API should be accessed from the {@link window.top}
 * since the JavaScript context is shared. We are unable to directly access properties on {@link window.top}
 * without seeking into {@link window.top} when inside of an `iframe`. Hence, we first mount the bridge API
 * onto the window using {@link mountAPI}, and then read it off {@link window.top} using {@link readAPI}.
 *
 * @param bridgeAPI instance to attach
 */
export const mountAPI = (bridgeAPI: RovoContentBridgeAPI) => {
	try {
		// do not mount unless we are the topmost window
		if (!isSameOrigin(window)) {
			return;
		}

		// do not override if already existing on topmost window
		if (window.top.__rovoContentBridgeAPI__) {
			return;
		}

		window.top.__rovoContentBridgeAPI__ = bridgeAPI;
		window.top.__rovoBridgeDebuggerStart__ = () => {
			import('./debug/index')
				.then(() => window.top?.__rovoBridgeDebugger__.enable())
				.catch((err) => console.error('failed to initialise rovo debug API', err));
		};
	} catch {
		// bail out if any errors take place (browser quirks on accessing window.top from a cross-origin frame)
		return;
	}
};

/**
 * Reads a given bridge API instance off {@link window.top} if the JavaScript context is shared.
 * @returns null if bridge API is not present OR JS context is not shared with top window; otherwise
 * yields the bridge API instance on {@link window.top}.
 */
export const readAPI = () => {
	try {
		if (isSameOrigin(window)) {
			return window.top.__rovoContentBridgeAPI__;
		} else {
			return null;
		}
	} catch {
		return null;
	}
};

/**
 * Clears shared reference to bridge API for GC purposes.
 */
export const clearAPI = () => {
	try {
		if (isSameOrigin(window)) {
			window.top.__rovoContentBridgeAPI__ = undefined as any;
			// Also clear debug API to prevent memory leaks
			window.top.__rovoBridgeDebuggerStart__ = undefined as any;
		}
	} catch {
		return;
	}
};
